import {
  motion,
  MotionValue,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";
import { memo, useRef, useState } from "react";
import { BrushTickFrequencyType, BrushTickValueType } from "./Brush.types";
import { formatTickTime, getOptimalFrequency } from "./Brush.utils";

import { Typography } from "@/components/ui/typography";

const BrushTick = memo(function BrushTick({
  tick,
  startTimeMotionValue,
  endTimeMotionValue,
  durationMotionValue,
}: {
  tick: BrushTickValueType;
  startTimeMotionValue: MotionValue<number>;
  endTimeMotionValue: MotionValue<number>;
  durationMotionValue: MotionValue<number>;
}) {
  const [frequency, setFrequency] = useState<BrushTickFrequencyType>("second");

  // Use transform to calculate position based on time motion value
  // This automatically updates when timeMotionValue changes, no re-renders needed
  const leftPosition = useTransform(() => {
    // For paused mode, position ticks relative to the current viewport center
    // For live mode, use the original logic
    let position =
      (tick.timestamp - startTimeMotionValue.get()) /
      (endTimeMotionValue.get() - startTimeMotionValue.get());

    position = Math.max(0, Math.min(1, position));
    return `${position * 100}%`;
  });

  useMotionValueEvent(durationMotionValue, "change", (duration) => {
    const frequency = getOptimalFrequency(duration);
    setFrequency(frequency);
  });

  return (
    <motion.div
      style={{
        width: "1px",
        backgroundColor: "var(--border)",
        height: "100%",
        position: "absolute",
        left: leftPosition,
        zIndex: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        paddingBottom: "4px",
      }}
    >
      <Typography
        level="body-md"
        className="text-muted-foreground/80 text-[10px] font-mono whitespace-nowrap transform-[translateX(-50%)] bg-muted p-[2px_4px] rounded-[4px] [border:1px_solid_var(--border)] [box-shadow:0_1px_2px_rgba(0,0,0,0.1)]"
      >
        {formatTickTime(tick.timestamp, frequency)}
      </Typography>
    </motion.div>
  );
});

export { BrushTick };
